import type { Theme } from '@onefootprint/design-tokens';
import themes from '@onefootprint/design-tokens';
import { cva } from 'class-variance-authority';
import type * as monaco from 'monaco-editor';

function themeColors(theme: Theme) {
  return {
    'editor.background': theme.backgroundColor.primary,
    'editor.foreground': theme.color.primary,
    // Hide inactive selection highlighting. This makes it so multiple rules
    // editors on the same page can't have multiple selections displayed. This
    // is a workaround since deselecting on blur refocuses the editor in some
    // cases.
    'editor.inactiveSelectionBackground': '#00000000',
    // Transparent border for focused elements. We use our own styling.
    focusBorder: '#00000000',
  };
}

function themeRules(theme: Theme) {
  return [
    {
      token: 'keyword',
      foreground: theme.codeHighlight['hljs-keyword'].color?.toString(),
    },
    {
      token: 'boolean',
      foreground: theme.codeHighlight['hljs-literal'].color?.toString(),
    },
    {
      token: 'identifier',
      // Default foreground color.
    },
    {
      token: 'operator',
      foreground: theme.codeHighlight['hljs-keyword'].color?.toString(),
    },
    {
      token: 'string',
      foreground: theme.codeHighlight['hljs-string'].color?.toString(),
    },
    {
      token: 'number',
      foreground: theme.codeHighlight['hljs-number'].color?.toString(),
    },
  ];
}

export function defineCustomTheme(monacoInstance: typeof monaco) {
  monacoInstance.editor.defineTheme('risk-rules-theme-light', {
    base: 'vs',
    inherit: false,
    rules: themeRules(themes.light),
    colors: themeColors(themes.light),
  });

  monacoInstance.editor.defineTheme('risk-rules-theme-dark', {
    base: 'vs-dark',
    inherit: false,
    rules: themeRules(themes.dark),
    colors: themeColors(themes.dark),
  });
}

export const editorStyles = cva([
  'monaco-editor-container',
  '[&_.monaco-editor]:!w-full max-w-full',
  '[&_.view-lines monaco-mouse-cursor-text]:!min-w-full',
  '[&_.monaco-editor_.suggest-widget]:!w-[450px]',
  '[&_.monaco-editor_.suggest-widget]:!overflow-hidden',
  '[&_.monaco-editor_.suggest-widget]:!border',
  '[&_.monaco-editor_.suggest-widget]:!border-tertiary',
  '[&_.monaco-editor_.suggest-widget]:!bg-primary',
  '[&_.monaco-editor_.suggest-widget_.monaco-list-row]:!text-body-2',
  '[&_.monaco-editor_.suggest-widget_.monaco-list-row]:!text-primary',
  '[&_.monaco-editor_.suggest-widget_.monaco-list-row.focused]:!bg-secondary',
  '[&_.monaco-editor_.suggest-widget_.suggest-details]:!bg-primary',
  '[&_.monaco-editor_.suggest-widget_.suggest-details]:!text-primary',
  '[&_.monaco-editor_.suggest-widget_.monaco-highlighted-label_.highlight]:!text-primary',
  '[&_.monaco-editor_.suggest-widget_.monaco-list]:!bg-primary',
  '[&_.monaco-editor_.squiggly-error]:!text-error',
  '[&_.monaco-editor_.monaco-editor-overlaymessage]:!font-family-default',
  '[&_.monaco-editor_.monaco-editor-overlaymessage]:!text-[14px]',
  '[&_.monaco-editor_.monaco-editor-overlaymessage_.message]:!bg-primary',
  '[&_.monaco-editor_.monaco-editor-overlaymessage_.message]:!font-family-default',
  '[&_.monaco-editor_.monaco-editor-overlaymessage_.message_p]:!text-secondary',
  '[&_.monaco-editor_.monaco-editor-overlaymessage_.message]:!px-2',
  '[&_.monaco-editor_.monaco-editor-overlaymessage_.message]:!border-primary',
  '[&_.monaco-editor_.monaco-editor-overlaymessage_.anchor.top]:!border-b-primary',
  '[&_.monaco-editor_.monaco-editor-overlaymessage_.anchor.below]:!border-t-primary',

  // Disable sliders
  '[&_.monaco-editor_.monaco-sash]:!cursor-default',
  '[&_.monaco-editor_.monaco-sash]:!pointer-events-none',

  '[&_.monaco-editor_.monaco-hover]:!bg-secondary',
  '[&_.monaco-editor_.monaco-hover]:!font-family-default',
  // Hide the status bar on problems that references problems and quick fixes.
  '[&_.monaco-editor_.monaco-hover_.status-bar]:!hidden',

  // Hide the cursor when the editor is not editable.
  '[&.not-editable_.cursor]:!hidden',
]);

export const editorClassName = editorStyles();

export const editorOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
  minimap: { enabled: false },
  fontSize: 12,
  lineNumbers: 'off',
  automaticLayout: true,
  scrollbar: {
    vertical: 'hidden',
    horizontal: 'hidden',
    verticalScrollbarSize: 0,
    horizontalScrollbarSize: 0,
    alwaysConsumeMouseWheel: false,
  },
  fontFamily: 'monospace',
  glyphMargin: false,
  folding: false,
  lineDecorationsWidth: 0,
  lineNumbersMinChars: 0,
  padding: {
    top: 0,
    bottom: 0,
  },
  overviewRulerBorder: false,
  overviewRulerLanes: 0,
  hideCursorInOverviewRuler: true,
  renderLineHighlight: 'none',
  fixedOverflowWidgets: true,
  scrollBeyondLastLine: false,
  wordWrap: 'on',
  wordWrapBreakBeforeCharacters: '(',
  wordWrapBreakAfterCharacters: ') ',
  acceptSuggestionOnCommitCharacter: true,
  acceptSuggestionOnEnter: 'on',
  accessibilitySupport: 'auto',
  accessibilityPageSize: 10,
  ariaLabel: 'Editor content',
  ariaRequired: false,
  screenReaderAnnounceInlineSuggestion: true,
  autoClosingBrackets: 'languageDefined',
  autoClosingComments: 'languageDefined',
  autoClosingDelete: 'auto',
  autoClosingOvertype: 'auto',
  autoClosingQuotes: 'languageDefined',
  autoIndent: 'full',
  autoSurround: 'languageDefined',
  bracketPairColorization: {
    enabled: true,
  },
  stickyTabStops: false,
  codeLens: true,
  codeLensFontFamily: '',
  codeLensFontSize: 0,
  colorDecorators: true,
  colorDecoratorsActivatedOn: 'clickAndHover',
  colorDecoratorsLimit: 500,
  columnSelection: false,
  comments: {
    insertSpace: true,
    ignoreEmptyLines: true,
  },
  contextmenu: false,
  copyWithSyntaxHighlighting: true,
  cursorBlinking: 'blink',
  cursorSmoothCaretAnimation: 'off',
  cursorStyle: 'line',
  cursorSurroundingLines: 0,
  cursorSurroundingLinesStyle: 'default',
  cursorWidth: 0,
  suggest: {
    insertMode: 'insert',
    filterGraceful: true,
    showIcons: true,
    showInlineDetails: true,
    localityBonus: true,
  },
  quickSuggestions: false,
  parameterHints: {
    enabled: false,
  },
  inlineSuggest: {
    enabled: false,
  },
  suggestOnTriggerCharacters: true,
  suggestSelection: 'first',
  wordBasedSuggestions: 'off',
  tabCompletion: 'on',
  guides: {
    indentation: false,
  },
  // Don't highlight all instances of selected word.
  occurrencesHighlight: 'off',
  selectionHighlight: false,
  // Disable auto-linking.
  links: false,
};
